import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api.service';

type BatchLine = {
  id: string;
  employeeFileNumber?: string;
  documentNumber?: string;
  customerName?: string;
  installmentNumber?: number;
  installmentFrom?: number;
  installmentTo?: number;
  installmentCount?: number;
  installmentIds?: string[];
  amount?: number;
  penaltyAmount?: number;
  totalAmount?: number;
  importedAmount?: number | null;
  status?: string;
  resultCode?: string | null;
};

type BatchDetail = {
  id: string;
  fileName?: string | null;
  status?: string;
  fileDate?: string;
  periodFrom?: string;
  periodTo?: string;
  lineCount?: number;
  selectedAmount?: number;
  hasFileContent?: boolean;
  hasResponseContent?: boolean;
  publicEntity?: { code?: string; name?: string };
  lines?: BatchLine[];
};

function money(n?: number | null) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

function batchStatusLabel(status?: string) {
  switch (status) {
    case 'GENERATED':
      return 'Generado';
    case 'IMPORTED':
      return 'Importado';
    case 'CLOSED':
      return 'Cerrado';
    case 'DRAFT':
      return 'Borrador';
    default:
      return status || '—';
  }
}

function lineStatusLabel(status?: string, total?: number, imported?: number | null) {
  if (status === 'PAID' && imported != null && total != null && imported > 0 && imported < total - 0.01) {
    return 'Parcial';
  }
  switch (status) {
    case 'PENDING':
      return 'Pendiente';
    case 'SENT':
      return 'Enviado';
    case 'PAID':
      return 'Cobrado';
    case 'REJECTED':
      return 'Rechazado';
    case 'RELEASED':
      return 'Liberado';
    default:
      return status || '—';
  }
}

function installmentLabel(row: BatchLine) {
  const ids = Array.isArray(row.installmentIds) ? row.installmentIds : [];
  const count = row.installmentCount ?? (ids.length || 1);
  if (count > 1 && row.installmentFrom != null) {
    return `${row.installmentFrom}–${row.installmentTo} (${count})`;
  }
  if (count > 1) return `${row.installmentNumber} (${count})`;
  return String(row.installmentNumber ?? '—');
}

type Props = {
  batchId: string;
  onClose?: () => void;
  compact?: boolean;
};

export function PayrollBatchDetailPanel({ batchId, onClose, compact }: Props) {
  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await apiService.getPayrollDiscountBatch(batchId);
      setBatch(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'No se pudo cargar el lote');
      setBatch(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, [batchId]);

  const lines = batch?.lines ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return lines.filter((l) => {
      if (statusFilter && l.status !== statusFilter) return false;
      if (!needle) return true;
      const hay = `${l.employeeFileNumber || ''} ${l.documentNumber || ''} ${l.customerName || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [lines, q, statusFilter]);

  const totals = useMemo(() => {
    let sent = 0;
    let paid = 0;
    let rejected = 0;
    for (const l of lines) {
      sent += Number(l.totalAmount) || 0;
      if (l.status === 'PAID') paid += Number(l.importedAmount ?? l.totalAmount) || 0;
      if (l.status === 'REJECTED') rejected += 1;
    }
    return { sent, paid, rejected, count: lines.length };
  }, [lines]);

  const hasSentFile = Boolean(batch?.hasFileContent || batch?.fileName);
  const hasResponse = Boolean(batch?.hasResponseContent);

  const downloadSent = async () => {
    try {
      await apiService.downloadPayrollDiscountFile(batchId);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'No se pudo descargar el enviado');
    }
  };

  const downloadResponse = async () => {
    try {
      await apiService.downloadPayrollDiscountResponseFile(batchId);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'No hay TXT de respuesta');
    }
  };

  return (
    <div className={`border rounded-lg bg-white ${compact ? 'p-3' : 'p-4'} space-y-4`}>
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {batch?.publicEntity?.code} — {batch?.publicEntity?.name || 'Lote'}
          </h3>
          <p className="text-sm text-gray-600">
            {batch?.fileName || batchId.slice(0, 10)} · {batchStatusLabel(batch?.status)}
            {batch?.fileDate ? ` · Archivo ${new Date(batch.fileDate).toLocaleDateString('es-AR')}` : ''}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Líneas {totals.count} · Enviado {money(totals.sent)} · Cobrado {money(totals.paid)} ·
            Rechazados {totals.rejected}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy || !hasSentFile}
            onClick={() => void downloadSent()}
            className="px-3 py-2 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
            title={!hasSentFile ? 'Todavía no hay archivo generado' : undefined}
          >
            Descargar enviado
          </button>
          <button
            type="button"
            disabled={busy || !hasResponse}
            onClick={() => void downloadResponse()}
            className="px-3 py-2 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
            title={!hasResponse ? 'Importá la respuesta de la entidad primero' : undefined}
          >
            Descargar respuesta
          </button>
          {onClose && (
            <button type="button" className="px-3 py-2 text-sm text-gray-500" onClick={onClose}>
              Cerrar
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      <div className="flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar legajo, DNI o cliente"
          className="px-3 py-2 border rounded-md text-sm min-w-[220px]"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="SENT">Enviado</option>
          <option value="PAID">Cobrado</option>
          <option value="REJECTED">Rechazado</option>
          <option value="PENDING">Pendiente</option>
          <option value="RELEASED">Liberado</option>
        </select>
      </div>

      <div className="overflow-x-auto border rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Legajo</th>
              <th className="px-3 py-2">DNI</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Cuota</th>
              <th className="px-3 py-2 text-right">Enviado</th>
              <th className="px-3 py-2 text-right">Respuesta</th>
              <th className="px-3 py-2">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {busy && !batch ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Cargando lote…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Sin líneas para mostrar
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{row.employeeFileNumber || '—'}</td>
                  <td className="px-3 py-2">{row.documentNumber || '—'}</td>
                  <td className="px-3 py-2">{row.customerName || '—'}</td>
                  <td className="px-3 py-2">{installmentLabel(row)}</td>
                  <td className="px-3 py-2 text-right">{money(row.totalAmount)}</td>
                  <td className="px-3 py-2 text-right">
                    {row.status === 'REJECTED'
                      ? money(0)
                      : row.importedAmount != null
                        ? money(row.importedAmount)
                        : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {lineStatusLabel(row.status, row.totalAmount, row.importedAmount)}
                    {row.resultCode ? (
                      <span className="text-xs text-gray-400 ml-1">({row.resultCode})</span>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
